import { useMemo } from 'react';
import type { Industry } from 'services/industryService';

// Mirrors the scope grammar in leadrs-back/src/services/marketplaceScope.ts
// (kept as a small local parser rather than a shared package, since front/back
// are separate deployables): <action> | <action>:industry:* |
// <action>:industry:<Industry> | <action>:industry:<Industry>:<SubIndustry>.

export type ScopeAction = 'view' | 'buy';

interface ParsedScope {
    action: ScopeAction;
    industry: '*' | string;
    subIndustry?: string;
}

const ACTION_LABELS: Record<ScopeAction, string> = { view: 'Consulter', buy: 'Acheter' };

export const parseScopeString = (scope: string): ParsedScope | null => {
    const match = scope.trim().match(/^(view|buy)(?::industry:(\*|[^:]+)(?::([^:]+))?)?$/);
    if (!match) return null;
    const [, action, industry, subIndustry] = match;
    return { action: action as ScopeAction, industry: industry ?? '*', ...(subIndustry ? { subIndustry } : {}) };
};

export const describeScope = (scope: string): string => {
    const parsed = parseScopeString(scope);
    if (!parsed) return scope;
    const actionLabel = ACTION_LABELS[parsed.action];
    if (parsed.industry === '*') return `${actionLabel} — toutes industries`;
    if (parsed.subIndustry) return `${actionLabel} — ${parsed.industry} / ${parsed.subIndustry}`;
    return `${actionLabel} — ${parsed.industry}`;
};

interface ScopeBuilderProps {
    industries: Industry[];
    value: string[];
    onChange: (scope: string[]) => void;
    actions?: ScopeAction[];
}

export default function ScopeBuilder({ industries, value, onChange, actions = ['view', 'buy'] }: ScopeBuilderProps) {
    const parsed = useMemo(
        () => value.map(parseScopeString).filter((p): p is ParsedScope => !!p),
        [value]
    );

    const isAllIndustries = (action: ScopeAction) => parsed.some((p) => p.action === action && p.industry === '*');
    const isIndustryChecked = (action: ScopeAction, industryName: string) =>
        isAllIndustries(action) || parsed.some((p) => p.action === action && p.industry === industryName && !p.subIndustry);
    const isSubIndustryChecked = (action: ScopeAction, industryName: string, subName: string) =>
        isIndustryChecked(action, industryName) ||
        parsed.some((p) => p.action === action && p.industry === industryName && p.subIndustry === subName);

    const toggleAllIndustries = (action: ScopeAction, checked: boolean) => {
        let next = value.filter((s) => parseScopeString(s)?.action !== action);
        if (checked) next = [...next, action];
        onChange(next);
    };

    const toggleIndustry = (action: ScopeAction, industryName: string, checked: boolean) => {
        if (isAllIndustries(action)) return;
        let next = value.filter((s) => {
            const p = parseScopeString(s);
            return !(p && p.action === action && p.industry === industryName);
        });
        if (checked) next = [...next, `${action}:industry:${industryName}`];
        onChange(next);
    };

    const toggleSubIndustry = (action: ScopeAction, industryName: string, subName: string, checked: boolean) => {
        if (isIndustryChecked(action, industryName)) return;
        let next = value.filter((s) => {
            const p = parseScopeString(s);
            return !(p && p.action === action && p.industry === industryName && p.subIndustry === subName);
        });
        if (checked) next = [...next, `${action}:industry:${industryName}:${subName}`];
        onChange(next);
    };

    return (
        <div className="flex flex-col gap-4">
            {actions.map((action) => (
                <div key={action} className="rounded-lg border border-leadrs-stroke p-3">
                    <label className="flex items-center gap-2 font-geist text-sm font-semibold text-leadrs-text">
                        <input
                            type="checkbox"
                            checked={isAllIndustries(action)}
                            onChange={(e) => toggleAllIndustries(action, e.target.checked)}
                        />
                        {ACTION_LABELS[action]} — toutes les industries
                    </label>
                    <div className="mt-2 flex max-h-64 flex-col gap-1 overflow-y-auto pl-5">
                        {industries.map((industry) => (
                            <div key={industry.name}>
                                <label
                                    className={`flex items-center gap-2 font-geist text-sm ${
                                        isAllIndustries(action) ? 'text-leadrs-text-soft' : 'text-leadrs-text'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        disabled={isAllIndustries(action)}
                                        checked={isIndustryChecked(action, industry.name)}
                                        onChange={(e) => toggleIndustry(action, industry.name, e.target.checked)}
                                    />
                                    {industry.name}
                                </label>
                                {industry.subIndustries?.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 pl-6">
                                        {industry.subIndustries.map((sub) => (
                                            <label
                                                key={sub.name}
                                                className={`flex items-center gap-1.5 font-geist text-xs ${
                                                    isIndustryChecked(action, industry.name)
                                                        ? 'text-leadrs-text-soft'
                                                        : 'text-leadrs-text'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    disabled={isIndustryChecked(action, industry.name)}
                                                    checked={isSubIndustryChecked(action, industry.name, sub.name)}
                                                    onChange={(e) =>
                                                        toggleSubIndustry(action, industry.name, sub.name, e.target.checked)
                                                    }
                                                />
                                                {sub.name}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
